import React, { useEffect, useState } from 'react';
import useModalStore from '../../stores/modalStore';
import { datetimeLocalToEpoch, epochToIso8601 } from "../../utils/formatting";
import { LOCATION_NAME_TO_ID } from "../../utils/constants";
import {addEvent, loggedAlert} from "../../services/eventService";
import { EVENT_TYPE } from "../../domain";
import useUserStore from "../../stores/userStore";
import LoadingSpinner from "../../LoadingSpinner";
import GenericModal from "./GenericModal";

const AddEventModal = () => {
    const show = useModalStore(state => state.showAddEventModal);
    const modalProps = useModalStore(state => state.modalProps.addEventModal);
    const userFirstName = modalProps?.userFirstName || "user";
    const needsReview = modalProps?.needsReview;
    const userId = modalProps?.userId;
    const closeModal = useModalStore((state) => state.closeModal);
    const { currentUser, selectedLocation, setRequestList, setEmployeeEvents } = useUserStore();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [localEvent, setLocalEvent] = useState({
        user_id: userId,
        event_type: modalProps?.startingEventType,
        event_timestamp: Date.now(),
        insert_timestamp: Date.now(),
        notes: '',
        value: 0,
        created_by: currentUser?.user_id,
        location_id: selectedLocation,
        needs_review: needsReview,
        is_forgotten_event: modalProps?.isForgottenEvent
    });

    useEffect(() => {
        if (localEvent.event_type !== EVENT_TYPE.COMPED_SERVICE && localEvent.value !== 0) {
            setLocalEvent({...localEvent, value: 0});
        }
    }, [localEvent, setLocalEvent]);


    //when the 'show' state variable changes, reset the time and event type
    //Must have modalProps as a dependency to trigger this reset because we want changes to startingEventType to force a reset of the setLocalEvent
    useEffect(() => {
        const eventType = modalProps?.startingEventType;
        const overrideNeedsReview = eventType === EVENT_TYPE.COMPED_SERVICE ? true : needsReview;

        setLocalEvent({
            user_id: userId,
            event_type: eventType,
            event_timestamp: Date.now(),
            insert_timestamp: Date.now(),
            notes: '',
            value: 0,
            created_by: currentUser?.user_id,
            location_id: selectedLocation,
            needs_review: overrideNeedsReview,
            is_forgotten_event: modalProps?.isForgottenEvent
        });
    }, [show, userId, currentUser, selectedLocation, needsReview, modalProps]);

    if (!show) return null;

    return (
        <>
            <GenericModal
                name="addEventModal"
                title={`Add event for ${userFirstName || userId}`}
                modalProps={{ userId }}
                hideButton={true}>

                    <form
                        onSubmit={async (event) => {
                            event.preventDefault();

                            if(localEvent.is_forgotten_event && (localEvent.event_type === EVENT_TYPE.CLOCK_IN || localEvent.event_type === EVENT_TYPE.CLOCK_OUT) && localEvent.event_timestamp > Date.now()) {
                                alert("Cannot clock event with time greater than the current time. Fix your date/time selection.")
                                return
                            }

                            setIsSubmitting(true);
                            try {
                                const callback = async (newEvent) => {
                                    setEmployeeEvents(userId, prev => [newEvent, ...prev]);

                                    if (needsReview) {
                                        setRequestList(prev => [newEvent, ...prev]);
                                    }

                                    //TODO: update the status table for the person in question

                                    closeModal("addEventModal");
                                };
                                await addEvent(localEvent, callback);
                            } catch (error) {
                                console.error('Failed to add event:', error);
                                loggedAlert("Failed to add event. See console for details.", currentUser?.user_id, selectedLocation);
                            } finally {
                                setIsSubmitting(false);
                            }
                        }}
                        className="flex flex-col gap-4 p-2"
                    >
                        <div className="space-y-4">
                            <label className="block text-sm font-medium text-gray-700">
                                Event Type
                                <select
                                    id="event-type"
                                    className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm"
                                    value={localEvent.event_type}
                                    onChange={(e) => {
                                        setLocalEvent({ ...localEvent, event_type: e.target.value });
                                    }}
                                    required
                                >
                                    <option value={ EVENT_TYPE.NONE } disabled>Select an event type</option>
                                    <option value={ EVENT_TYPE.CLOCK_IN }>Clock In</option>
                                    <option value={ EVENT_TYPE.CLOCK_OUT }>Clock Out</option>
                                    <option value={ EVENT_TYPE.SICK }>Sick</option>
                                    <option value={ EVENT_TYPE.NOTE }>Note</option>
                                    <option value={ EVENT_TYPE.COMPED_SERVICE }>Comped Service</option>
                                </select>
                            </label>

                            {localEvent.event_type === EVENT_TYPE.COMPED_SERVICE && (
                                <label className="block text-sm font-medium text-gray-700">
                                    Dollar Value
                                    <input
                                        id="dollar-value"
                                        className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm"
                                        name="value"
                                        required
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={localEvent.value ?? ""}
                                        onChange={(e) =>
                                            setLocalEvent({ ...localEvent, value: parseFloat(e.target.value) })
                                        }
                                    />
                                </label>
                            )}

                            <label className="block text-sm font-medium text-gray-700">
                                Timestamp
                                <input
                                    id="timestamp"
                                    className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm"
                                    required
                                    type="datetime-local"
                                    value={epochToIso8601(localEvent.event_timestamp)}
                                    onChange={(e) => {
                                            const selectedDatetime = e.target.value;
                                            const selectedEpochMillis = datetimeLocalToEpoch(selectedDatetime);
                                            setLocalEvent({...localEvent, event_timestamp: selectedEpochMillis})
                                        }
                                    }
                                />
                            </label>

                            <label className="block text-sm font-medium text-gray-700">
                                Location
                                <select
                                    id="location"
                                    className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm"
                                    value={localEvent.location_id ?? ""}
                                    onChange={(e) =>
                                        setLocalEvent({ ...localEvent, location_id: parseInt(e.target.value, 10) })
                                    }
                                >
                                    <option value="" disabled>Select a location</option>
                                    {Object.entries(LOCATION_NAME_TO_ID)
                                        .filter(([_, id]) => id !== null)
                                        .map(([name, id]) => (
                                            <option key={id} value={id}>{name}</option>
                                        ))}
                                </select>
                            </label>

                            <label className="block text-sm font-medium text-gray-700">
                                Notes
                                <textarea
                                    id="notes"
                                    className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm resize-y min-h-[60px]"
                                    value={localEvent.notes}
                                    placeholder="Optional notes..."
                                    onChange={(e) => setLocalEvent({ ...localEvent, notes: e.target.value })}
                                ></textarea>
                            </label>
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => closeModal("addEventModal")}
                                disabled={isSubmitting}
                                className="!px-4 !py-2 !text-sm !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-xl hover:!bg-gray-50 !transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="!px-4 !py-2 !text-sm !font-semibold !text-white !bg-indigo-600 hover:!bg-indigo-700 !border-none !rounded-xl !shadow-sm hover:!shadow-md !transition-all !inline-flex !items-center !gap-2"
                            >
                                {isSubmitting ? <LoadingSpinner small /> : "Add Event"}
                            </button>
                        </div>
                    </form>
            </GenericModal>
        </>
    );
};

export default AddEventModal;
